import { Directive, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FirestoreService } from '@app-services/deployments/firestore.service';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { NewVersionAvailableComponent } from '@shared/components/new-version-available/new-version-available.component';
import { deploymentMessage } from '@shared/components/new-version-available/messages';
import { environment } from '@environments/environment';

@Directive()
export abstract class SoftwareUpdate implements OnInit, OnDestroy {
  private ngUnsubscribe$: Subject<void> = new Subject();
  private previousActiveState: boolean | null = null;

  constructor(
    protected firestoreService: FirestoreService,
    protected router: Router,
    protected dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.checkActiveDeployment();
    this.checkAppVersion();
  }

  private checkActiveDeployment(): void {
    this.firestoreService
      .getActiveDeployment()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data: any) => {
        const currentActiveState = data?.active;

        if (this.previousActiveState === null) {
          this.previousActiveState = currentActiveState;
          if (currentActiveState) {
            this.router.navigate(['/active-deployment']);
          }
        } else if (this.previousActiveState !== currentActiveState) {
          if (currentActiveState) {
            this.router.navigate(['/active-deployment']);
          } else {
            const hasReloaded = sessionStorage.getItem('hasReloaded');
            if (!hasReloaded) {
              sessionStorage.setItem('hasReloaded', 'true');
              this.showConfirmationDialog(deploymentMessage.message);
            }
          }
          this.previousActiveState = currentActiveState;
        }
      });
  }

  private checkAppVersion(): void {
    this.firestoreService
      .getAppVersion()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((versionData: any) => {
        if (!versionData) return;

        const currentVersion = versionData.version;
        const lastKnownVersion = localStorage.getItem('appVersion');

        if (
          currentVersion &&
          lastKnownVersion &&
          currentVersion !== lastKnownVersion
        ) {
          localStorage.setItem('appVersion', currentVersion);

          const isPortalAffected =
            versionData.portals &&
            Array.isArray(versionData.portals) &&
            versionData.portals.includes(environment.PORTAL);

          if (isPortalAffected) {
            const message =
              versionData.notificationMessage || deploymentMessage.message;
            this.showConfirmationDialog(message);
          }
        } else if (currentVersion) {
          localStorage.setItem('appVersion', currentVersion);
        }
      });
  }

  protected showConfirmationDialog(message: string): void {
    this.dialog
      .open(NewVersionAvailableComponent, {
        panelClass: 'message-modal-version',
        data: { ...deploymentMessage, message },
        disableClose: true,
      })
      .afterClosed()
      .subscribe(() => {
        location.reload();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
